import type { ApartmentImage } from '@/lib/apartments/apartmentGallery'
import { normalizeApartmentTourUrl } from '@/lib/apartments/apartmentGallery'
import type { Location, Media } from '@/payload-types'
import { getMediaUrl } from '@/utilities/getMediaUrl'

export type CommunitySpaceTab = 'gallery' | 'virtualTour' | 'floorPlans'

export const COMMUNITY_SPACE_TAB_ORDER: CommunitySpaceTab[] = [
  'gallery',
  'virtualTour',
  'floorPlans',
]

export type CommunitySpaceVirtualTour = {
  embedUrl: string
  title: string
}

export type LocationCommunitySpaces = {
  floorPlans: ApartmentImage[]
  gallery: ApartmentImage[]
  tabs: CommunitySpaceTab[]
  virtualTour: CommunitySpaceVirtualTour | null
}

function resolveMedia(media: number | Media | null | undefined): Media | null {
  if (!media || typeof media === 'number') return null
  return media
}

function mapMediaToImage(
  media: Media | null,
  fallbackAlt: string,
): ApartmentImage | null {
  const src = getMediaUrl(media?.url)
  if (!src) return null

  return {
    alt: media?.alt?.trim() || fallbackAlt,
    src,
    ...(media?.height != null ? { height: media.height } : {}),
    ...(media?.width != null ? { width: media.width } : {}),
  }
}

function mapCommunitySpaceImageRows(
  rows: Array<{ image?: number | Media | null }> | null | undefined,
  fallbackAlt: string,
): ApartmentImage[] {
  const images: ApartmentImage[] = []

  for (const row of rows ?? []) {
    const image = mapMediaToImage(resolveMedia(row.image), fallbackAlt)
    if (image) {
      images.push(image)
    }
  }

  return images
}

export function mapLocationCommunitySpaces(
  communitySpaces: Location['communitySpaces'] | null | undefined,
  fallbackAlt: string,
): LocationCommunitySpaces | null {
  if (!communitySpaces) return null

  const gallery = mapCommunitySpaceImageRows(communitySpaces.gallery, fallbackAlt)
  const floorPlans = mapCommunitySpaceImageRows(communitySpaces.floorPlans, fallbackAlt)

  const tourUrl = normalizeApartmentTourUrl(communitySpaces.virtualTour?.tourUrl)
  const virtualTour = tourUrl
    ? {
        embedUrl: tourUrl,
        title: communitySpaces.virtualTour?.title?.trim() || fallbackAlt,
      }
    : null

  const tabs = getEnabledCommunitySpaceTabs({ gallery, virtualTour, floorPlans })

  if (tabs.length === 0) {
    return null
  }

  return {
    gallery,
    floorPlans,
    virtualTour,
    tabs,
  }
}

export function getEnabledCommunitySpaceTabs(
  content: Pick<LocationCommunitySpaces, 'gallery' | 'virtualTour' | 'floorPlans'>,
): CommunitySpaceTab[] {
  const tabs: CommunitySpaceTab[] = []

  if (content.gallery.length > 0) {
    tabs.push('gallery')
  }

  if (content.virtualTour) {
    tabs.push('virtualTour')
  }

  if (content.floorPlans.length > 0) {
    tabs.push('floorPlans')
  }

  return tabs
}

export function hasCommunitySpaceContent(
  communitySpaces: LocationCommunitySpaces | null | undefined,
): boolean {
  return Boolean(communitySpaces?.tabs.length)
}

export function isCommunitySpaceImageCarouselTab(
  tab: CommunitySpaceTab,
): tab is 'gallery' | 'floorPlans' {
  return tab === 'gallery' || tab === 'floorPlans'
}

export function getCommunitySpaceSlides(
  communitySpaces: LocationCommunitySpaces,
  activeTab: CommunitySpaceTab,
): ApartmentImage[] {
  if (activeTab === 'gallery') {
    return communitySpaces.gallery
  }

  if (activeTab === 'floorPlans') {
    return communitySpaces.floorPlans
  }

  if (activeTab === 'virtualTour' && communitySpaces.virtualTour) {
    const preview = communitySpaces.gallery[0]
    return preview ? [preview] : []
  }

  return []
}
